import React from 'react';
import {StyleProp, StyleSheet, View, ViewStyle} from 'react-native';
import Svg, {Rect} from 'react-native-svg';

import AmexIcon from '../../../../images/brand/svg/icons/Amex.svg';
import ApplePayIconSvg from '../../../../images/brand/svg/icons/ApplePay.svg';
import GooglePayIconSvg from '../../../../images/brand/svg/icons/GooglePay.svg';
import MastercardIcon from '../../../../images/brand/svg/icons/Mastercard.svg';
import VisaIcon from '../../../../images/brand/svg/icons/Visa.svg';

type IconSize = 'small' | 'medium';

/**
 * Payment Icon Sizing:
 *
 * Apple Pay, Visa, Mastercard and Amex use a unified 70x48 chip viewBox
 * (aspect ratio ~1.458) whose artwork fills the box. On a white surface their
 * white chip fill disappears, so only the inner brand logo reads.
 *
 * Google Pay is the official, unmodified Google Pay Mark (1094x742 viewBox).
 * Its viewBox bakes in mandatory clear space around the pill (>=0.5x the super-G
 * height on every side; the approved artwork ships ~2x that), which we must not
 * crop or re-draw. Because that clear space makes the pill read small at the peer
 * box, GooglePayIcon upscales the mark inside a fixed peer-box container and lets
 * the transparent clear space overflow (clipped) — a layout-only fix that leaves
 * the SVG untouched. See GooglePayIcon below.
 *
 * Apple Pay minimum height requirement: 30pt.
 */

// Payment icon heights - Apple Pay minimum is 30pt
const PAYMENT_ICON_HEIGHTS = {
  small: 24,
  medium: 30, // Apple Pay minimum requirement
};

// Unified aspect ratio for all payment icons: 70/48 ≈ 1.458
const PAYMENT_ICON_ASPECT = 70 / 48;

// Generic card fallback aspect ratio
const GENERIC_CARD_ASPECT = 40 / 25;

// Apple Pay icon
export function ApplePayIcon({size = 'medium'}: {size?: IconSize}) {
  const height = PAYMENT_ICON_HEIGHTS[size];
  const width = Math.round(height * PAYMENT_ICON_ASPECT);
  return (
    <View style={styles.iconContainer}>
      <ApplePayIconSvg width={width} height={height} />
    </View>
  );
}

// Full Google Pay mark aspect (incl. its baked-in clear space): 1094/742 ≈ 1.474
const GPAY_MARK_ASPECT = 1094 / 742;
// The pill spans this fraction of the full mark width; the rest is clear space.
const GPAY_PILL_WIDTH_RATIO = 752 / 1094;

// Google Pay icon — official Google Pay Mark, rendered UNMODIFIED.
// The mark reserves generous clear space inside its viewBox, so at the peer box
// the pill reads much smaller than the peer logos. We must not touch the approved
// artwork, so we fix it purely in layout: render the mark large enough that its
// pill spans the icon width like the peer chips, then keep the container at the
// peer box and clip the (transparent) clear space that overflows. The SVG is
// byte-for-byte unchanged — only the container CSS scales the visible pill.
export function GooglePayIcon({size = 'medium'}: {size?: IconSize}) {
  const height = PAYMENT_ICON_HEIGHTS[size];
  const width = Math.round(height * PAYMENT_ICON_ASPECT);
  // Upscale the mark so the pill fills the icon width; clear space overflows.
  const markWidth = Math.round(width / GPAY_PILL_WIDTH_RATIO);
  const markHeight = Math.round(markWidth / GPAY_MARK_ASPECT);
  return (
    <View style={[styles.iconContainer, {width, height, overflow: 'hidden'}]}>
      <View
        style={{
          position: 'absolute',
          left: (width - markWidth) / 2,
          top: (height - markHeight) / 2,
        }}>
        <GooglePayIconSvg width={markWidth} height={markHeight} />
      </View>
    </View>
  );
}

// Generic credit card icon fallback
export function CreditCardIcon({size = 'medium'}: {size?: IconSize}) {
  const height = PAYMENT_ICON_HEIGHTS[size];
  const width = Math.round(height * GENERIC_CARD_ASPECT);
  return (
    <View style={styles.iconContainer}>
      <Svg width={width} height={height} viewBox="0 0 40 25">
        <Rect
          x="1"
          y="1"
          width="38"
          height="23"
          rx="3"
          fill="#E8E8E8"
          stroke="#CCC"
          strokeWidth="1"
        />
        <Rect x="1" y="5" width="38" height="5" fill="#333" />
        <Rect x="4" y="14" width="12" height="3" rx="1" fill="#CCC" />
        <Rect x="4" y="18" width="8" height="2" rx="1" fill="#DDD" />
      </Svg>
    </View>
  );
}

// Card brand icon by type - unified sizing
export function CardBrandIcon({
  cardType,
  size = 'medium',
  style,
}: {
  cardType?: string;
  size?: IconSize;
  style?: StyleProp<ViewStyle>;
}) {
  const height = PAYMENT_ICON_HEIGHTS[size];
  const width = Math.round(height * PAYMENT_ICON_ASPECT);
  const type = cardType?.toLowerCase();

  if (type === 'visa') {
    return (
      <View style={[styles.iconContainer, style]}>
        <VisaIcon width={width} height={height} />
      </View>
    );
  }
  if (type === 'mastercard') {
    return (
      <View style={[styles.iconContainer, style]}>
        <MastercardIcon width={width} height={height} />
      </View>
    );
  }
  if (type === 'amex' || type === 'american express') {
    return (
      <View style={[styles.iconContainer, style]}>
        <AmexIcon width={width} height={height} />
      </View>
    );
  }

  // Default generic card
  return <CreditCardIcon size={size} />;
}

export function SupportedCardIcons({
  size = 'medium',
  supportedCardBrands = ['visa', 'mastercard', 'amex'],
}: {
  size?: IconSize;
  supportedCardBrands?: string[];
}) {
  // Only render brands we have specific icons for to avoid generic card icon in the list
  const renderableBrands = supportedCardBrands.filter(brand =>
    ['visa', 'mastercard', 'amex', 'american express'].includes(brand.toLowerCase()),
  );

  return (
    <View style={styles.supportedCardsRow}>
      {renderableBrands.map((brand, index) => (
        <CardBrandIcon
          key={`${brand}-${index}`}
          cardType={brand}
          size={size}
          style={styles.cardBrandIcon}
        />
      ))}
    </View>
  );
}

// Container width based on medium height (30) * aspect ratio (70/48) ≈ 44px
const ICON_CONTAINER_WIDTH = Math.round(PAYMENT_ICON_HEIGHTS.medium * PAYMENT_ICON_ASPECT);

const styles = StyleSheet.create({
  iconContainer: {
    width: ICON_CONTAINER_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
  },
  supportedCardsRow: {
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  cardBrandIcon: {
    // All icons now share the same dimensions, no override needed
    width: 'auto',
  },
});
